import type { TestEvent } from '../types/index.js';
import type { KafkaEventProducer } from './kafka.js';
import type { HttpClient } from './http.js';
import type { Logger } from '../utils/logger.js';
import { createNoopLogger } from '../utils/logger.js';
import {
  EventTooLargeError,
  HttpDeliveryError,
  PartialBatchError,
  isPermanentHttpStatus,
  isQuotaError,
} from '../reporter/errors.js';
import { sizeOf } from '../utils/sizeOf.js';

// Sit under the server's 1000-event / 5-MiB per-POST caps with headroom for
// JSON envelope, proxies, and auth headers.
const HTTP_CHUNK_MAX_EVENTS = 900;
const HTTP_CHUNK_MAX_BYTES = 4 * 1024 * 1024;

export interface DeliveryManagerOptions {
  kafkaProducer?: KafkaEventProducer;
  httpClient: HttpClient;
  httpFallbackUrl?: string;
  maxAttempts?: number;
  baseDelayMs?: number;
  kafkaRecoveryThreshold?: number;
  logger?: Logger;
}

type DeliveryChannel = 'kafka' | 'http';

export class EventDeliveryManager {
  private readonly kafkaProducer: KafkaEventProducer | null;
  private readonly httpClient: HttpClient;
  private readonly httpFallbackUrl: string | null;
  private readonly maxAttempts: number;
  private readonly baseDelayMs: number;
  private readonly logger: Logger;

  private kafkaHealthy = true;
  private readonly kafkaRecoveryThreshold: number;
  private httpSuccessCount = 0;
  private discardedCount = 0;

  lastDeliveryAt = 0;

  constructor(options: DeliveryManagerOptions) {
    this.kafkaProducer = options.kafkaProducer ?? null;
    this.httpClient = options.httpClient;
    this.httpFallbackUrl = options.httpFallbackUrl ?? null;
    this.maxAttempts = options.maxAttempts ?? 3;
    this.baseDelayMs = options.baseDelayMs ?? 500;
    this.logger = options.logger ?? createNoopLogger();
    this.kafkaRecoveryThreshold = options.kafkaRecoveryThreshold ?? 3;
  }

  async sendBatch(events: TestEvent[]): Promise<void> {
    if (events.length === 0) return;

    if (this.kafkaProducer && this.kafkaHealthy) {
      const kafkaSuccess = await this.tryKafkaWithRetry(events);
      if (kafkaSuccess) {
        this.lastDeliveryAt = Date.now();
        return;
      }
      this.kafkaHealthy = false;
      this.logger.warn('Kafka failed — switching to HTTP fallback');
    }

    await this.tryHttpWithRetry(events);
  }

  // run:end goes through here direct, never buffered — must stay last on the partition.
  async send(event: TestEvent): Promise<void> {
    await this.sendBatch([event]);
  }

  // One attempt per channel: the mid-run backoff alone outlasts the shutdown
  // budget, so the HTTP fallback never starts.
  async sendUrgent(events: TestEvent[]): Promise<void> {
    if (events.length === 0) return;

    if (this.kafkaProducer && this.kafkaHealthy) {
      if (await this.tryKafkaWithRetry(events, 1)) {
        this.lastDeliveryAt = Date.now();
        return;
      }
      this.kafkaHealthy = false;
      this.logger.warn('Kafka failed — switching to HTTP fallback');
    }

    await this.tryHttpWithRetry(events, 1);
  }

  private async tryKafkaWithRetry(events: TestEvent[], maxAttempts = this.maxAttempts): Promise<boolean> {
    if (!this.kafkaProducer) return false;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        if (!this.kafkaProducer.isConnected) {
          this.logger.debug(`Kafka reconnect attempt ${attempt}/${maxAttempts}`);
          await this.kafkaProducer.connect();
        }

        await this.kafkaProducer.sendBatch(events);

        const first = events[0];
        const last = events[events.length - 1];
        const seqRange = 'sequence' in first ? `(seq ${first.sequence}-${(last as typeof first).sequence})` : '';
        this.logger.debug(`Flushed ${events.length} events via kafka ${seqRange}`.trim());
        return true;
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        if (attempt < maxAttempts) {
          const delay = this.getBackoffDelay(attempt);
          this.logger.debug(
            `Kafka send failed: ${message} (attempt ${attempt}/${maxAttempts}, retrying in ${delay}ms)`
          );
          await this.sleep(delay);
        } else {
          this.logger.error(`Kafka delivery failed after ${maxAttempts} attempts: ${message}`);
        }
      }
    }

    return false;
  }

  private async tryHttpWithRetry(events: TestEvent[], maxAttempts = this.maxAttempts): Promise<void> {
    if (!this.httpFallbackUrl) {
      throw new Error('HTTP fallback not configured — server did not return httpFallback URL');
    }

    const fallbackUrl = this.httpFallbackUrl;
    const subBatches = chunkForHttp(events);
    let succeeded = 0;

    for (let i = 0; i < subBatches.length; i++) {
      const subBatch = subBatches[i];
      try {
        await this.sendSubBatchWithSplit(fallbackUrl, subBatch, maxAttempts);
        succeeded += subBatch.length;
      } catch (error) {
        if (error instanceof EventTooLargeError) {
          this.discardedCount += error.count;
          continue;
        }
        // Quota must propagate raw so isQuotaError() upstream still matches;
        // wrapping in PartialBatchError would hide it and cause an infinite
        // restore-and-retry loop.
        if (isQuotaError(error)) throw error;
        // With prior sub-batches acked, wrap the un-attempted tail so the
        // buffer restores only what wasn't sent.
        if (succeeded === 0) throw error;
        const failed = subBatches.slice(i).flat();
        const message = error instanceof Error ? error.message : String(error);
        throw new PartialBatchError(
          failed,
          succeeded,
          `HTTP fallback failed at sub-batch ${i + 1}/${subBatches.length}: ${message}`,
          {
            cause: error,
          }
        );
      }
    }

    const first = events[0];
    const last = events[events.length - 1];
    const seqRange =
      first && last && 'sequence' in first ? `(seq ${first.sequence}-${(last as typeof first).sequence})` : '';
    this.logger.debug(
      `Flushed ${events.length} events via http in ${subBatches.length} sub-batches ${seqRange}`.trim()
    );
    this.lastDeliveryAt = Date.now();

    if (!this.kafkaHealthy) {
      this.httpSuccessCount++;
      if (this.httpSuccessCount >= this.kafkaRecoveryThreshold) {
        this.logger.debug(
          `HTTP success ${this.kafkaRecoveryThreshold}/${this.kafkaRecoveryThreshold} — probing Kafka recovery`
        );
        this.tryKafkaRecovery().catch(() => {});
      }
    }
  }

  private async sendSubBatchWithSplit(url: string, events: TestEvent[], maxAttempts = this.maxAttempts): Promise<void> {
    let lastError: Error | undefined;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        await this.httpClient.sendEvents(url, events);
        return;
      } catch (error) {
        if (error instanceof HttpDeliveryError && error.status === 413 && events.length === 1) {
          this.logger.error(`Single event exceeds server cap (413) — discarding one event`);
          throw new EventTooLargeError('event exceeds server per-request cap', { cause: error });
        }
        // Halve and recurse. A too-large half is swallowed so the peer half
        // still ships; any other error propagates (server upserts absorb any
        // duplicate re-send of the sibling).
        if (error instanceof HttpDeliveryError && error.status === 413) {
          const mid = Math.floor(events.length / 2);
          this.logger.debug(`413 on ${events.length}-event sub-batch — halving into ${mid} + ${events.length - mid}`);
          const halves = [events.slice(0, mid), events.slice(mid)];
          let allTooLarge = true;
          let tooLargeCount = 0;
          for (const half of halves) {
            try {
              await this.sendSubBatchWithSplit(url, half, maxAttempts);
              allTooLarge = false;
            } catch (halfError) {
              if (halfError instanceof EventTooLargeError) {
                tooLargeCount += halfError.count;
                continue;
              }
              throw halfError;
            }
          }
          if (allTooLarge) {
            throw new EventTooLargeError('all halves exceed server cap', { cause: error, count: tooLargeCount });
          }
          if (tooLargeCount > 0) this.discardedCount += tooLargeCount;
          return;
        }
        if (error instanceof HttpDeliveryError && isPermanentHttpStatus(error.status)) {
          this.logger.error(`HTTP fallback permanent error (${error.status}) — sub-batch discarded`);
          throw error;
        }
        if (isQuotaError(error)) {
          this.logger.error(`HTTP fallback permanent error (402 quota) — sub-batch discarded`);
          throw error;
        }

        lastError = error instanceof Error ? error : new Error(String(error));
        if (attempt < maxAttempts) {
          const delay = this.getBackoffDelay(attempt);
          this.logger.debug(
            `HTTP sub-batch attempt ${attempt}/${maxAttempts} failed: ${lastError.message} (retrying in ${delay}ms)`
          );
          await this.sleep(delay);
        } else {
          this.logger.error(`HTTP sub-batch delivery failed after ${maxAttempts} attempts: ${lastError.message}`);
        }
      }
    }

    throw lastError ?? new Error('HTTP sub-batch delivery failed — no attempts made (maxAttempts must be >= 1)');
  }

  private async tryKafkaRecovery(): Promise<void> {
    if (!this.kafkaProducer) return;
    this.httpSuccessCount = 0;
    try {
      await this.kafkaProducer.connect();
      this.kafkaHealthy = true;
      this.logger.debug('Kafka connection recovered — resuming primary channel');
    } catch {
      this.logger.debug('Kafka recovery failed — staying on HTTP');
    }
  }

  private getBackoffDelay(attempt: number): number {
    return this.baseDelayMs * Math.pow(2, attempt - 1);
  }

  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  get activeChannel(): DeliveryChannel {
    if (!this.kafkaProducer) return 'http';
    return this.kafkaHealthy ? 'kafka' : 'http';
  }

  get discardCount(): number {
    return this.discardedCount;
  }

  // Monotonic — 0/negative counts are dropped so a caller bug can't silently
  // un-fail a run.
  recordDiscard(count: number): void {
    if (count > 0) this.discardedCount += count;
  }
}

// FIFO across sub-batches; a lone event over the byte cap gets its own chunk
// and trips EventTooLargeError at send time.
function chunkForHttp(events: TestEvent[]): TestEvent[][] {
  if (events.length === 0) return [];
  const chunks: TestEvent[][] = [];
  let current: TestEvent[] = [];
  let currentBytes = 0;

  for (const event of events) {
    const eventBytes = sizeOf(event);
    const wouldExceedBytes = currentBytes + eventBytes > HTTP_CHUNK_MAX_BYTES && current.length > 0;
    const wouldExceedCount = current.length >= HTTP_CHUNK_MAX_EVENTS;
    if (wouldExceedBytes || wouldExceedCount) {
      chunks.push(current);
      current = [];
      currentBytes = 0;
    }
    current.push(event);
    currentBytes += eventBytes;
  }
  if (current.length > 0) chunks.push(current);
  return chunks;
}
